import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import type { ThreadMessage } from '@/hooks/useThreadMessages';
import type { ConversationMessage } from '@/hooks/useConversationHistory';
import type { IntegrationState } from '@/lib/integrations/integration-state';
import {
  CheckCircle2,
  Clock,
  EyeOff,
  Info,
  XCircle,
} from 'lucide-react';
import { formatUiDateTime, formatUiRelativeTime } from '@/lib/ui-format';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

interface ThreadTimelineProps {
  messages: ThreadMessage[];
  initialOutreach: string | null;
  conversationMessages?: ConversationMessage[];
  isLoadingHistory?: boolean;
}

// codex-review .contract-lens r12 F1: this alert used to render ONLY inside ThreadTimeline, but
// inbox.tsx mounts ThreadTimeline exclusively when threadMessages.length > 1 — a thread with
// exactly one message (the common first-LGM-inbound shape, api/os/webhooks/lgm.ts's single draft
// insert) took the sibling single-bubble branch instead and never saw this alert at all, even
// though the endpoint had already returned a `captured_only`/`not_connected` integration state.
// Extracted so inbox.tsx can render it once, above BOTH branches, regardless of message count.
export function IntegrationStateAlert({
  integrationState,
}: {
  integrationState?: IntegrationState;
}): JSX.Element | null {
  if (integrationState?.status === 'not_connected') {
    return (
      <Alert>
        <Info className="h-4 w-4" aria-hidden="true" />
        <AlertDescription>
          Full LinkedIn history is unavailable — the LinkedIn integration is
          not connected. Messages captured by Tacit are shown below.
        </AlertDescription>
      </Alert>
    );
  }

  if (integrationState?.status === 'captured_only') {
    return (
      <Alert>
        <Info className="h-4 w-4" aria-hidden="true" />
        <AlertDescription>
          Full conversation history is unavailable for this thread — it was
          captured through LGM, which has no API for retrieving message
          history. Messages captured by Tacit are shown below.
        </AlertDescription>
      </Alert>
    );
  }

  return null;
}

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

function formatDateTime(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  if (Number.isNaN(date.getTime())) {
    return 'Unknown time';
  }
  const diffMs = now.getTime() - date.getTime();
  const diffHours = diffMs / (1000 * 60 * 60);

  if (diffHours < 24) {
    return formatUiRelativeTime(date, now);
  }

  return formatUiDateTime(date, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function getStatusIcon(status: string, isSuperSeded: boolean): JSX.Element | null {
  if (isSuperSeded) return <EyeOff className="h-3 w-3 text-muted-foreground" />;
  switch (status) {
    case 'sent':
      return <CheckCircle2 className="h-3 w-3 text-green-500" />;
    case 'pending_approval':
      return <Clock className="h-3 w-3 text-orange-500" />;
    case 'rejected':
      return <XCircle className="h-3 w-3 text-red-500" />;
    case 'failed':
      return <XCircle className="h-3 w-3 text-red-500" />;
    default:
      return null;
  }
}

// Find a matching sent draft for a HeyReach "us" message (by content similarity)
function findMatchingDraft(
  msg: ConversationMessage,
  sentDrafts: ThreadMessage[]
): ThreadMessage | undefined {
  const msgContent = msg.content.trim().toLowerCase();
  return sentDrafts.find((d) => {
    const draftContent = (d.edited_response || d.draft_response).trim().toLowerCase();
    // Exact match or one contains the other (handles minor formatting differences)
    return draftContent === msgContent
      || msgContent.includes(draftContent)
      || draftContent.includes(msgContent);
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Loading skeleton
// ─────────────────────────────────────────────────────────────────────────────

function TimelineSkeleton(): JSX.Element {
  return (
    <div className="flex flex-col gap-3 py-4">
      <div className="flex justify-start">
        <Skeleton className="h-16 w-[60%] rounded-lg" />
      </div>
      <div className="flex justify-end">
        <Skeleton className="h-16 w-[60%] rounded-lg" />
      </div>
      <div className="flex justify-start">
        <Skeleton className="h-12 w-[50%] rounded-lg" />
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// HeyReach Conversation View (primary, when available)
// ─────────────────────────────────────────────────────────────────────────────

function ConversationView({
  conversationMessages,
  sentDrafts,
}: {
  conversationMessages: ConversationMessage[];
  sentDrafts: ThreadMessage[];
}): JSX.Element {
  return (
    <>
      {conversationMessages.map((msg, idx) => {
        const isUs = msg.sender === 'us';
        const matchingDraft = isUs ? findMatchingDraft(msg, sentDrafts) : undefined;

        return (
          <div
            key={`conv-${idx}-${msg.sentAt}`}
            className={`flex ${isUs ? 'justify-end' : 'justify-start'}`}
          >
            <div className="max-w-[80%] space-y-1">
              <div
                className={`rounded-lg p-3 text-sm ${
                  isUs
                    ? 'bg-primary/10 text-foreground'
                    : 'bg-muted text-foreground'
                }`}
              >
                <p
                  className="whitespace-pre-wrap"
                  data-content-origin="external"
                >
                  {msg.content}
                </p>

                {/* Draft metadata for our sent messages */}
                {matchingDraft && matchingDraft.methodology_used && (
                  <div className="mt-2 flex items-center gap-2">
                    <CheckCircle2 className="h-3 w-3 text-green-500" />
                    <Badge variant="outline" className="text-[10px]">
                      {matchingDraft.methodology_used}
                    </Badge>
                    {matchingDraft.confidence > 0 && (
                      <span className="text-[10px] text-muted-foreground">
                        {Math.round(matchingDraft.confidence * 100)}%
                      </span>
                    )}
                  </div>
                )}
              </div>
              <span className="text-[10px] text-muted-foreground">
                {formatDateTime(msg.sentAt)}
              </span>
            </div>
          </div>
        );
      })}
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Draft-based fallback view (when HeyReach data unavailable)
// ─────────────────────────────────────────────────────────────────────────────

function DraftFallbackView({
  messages,
}: {
  messages: ThreadMessage[];
}): JSX.Element {
  // Exclude active pending draft and awaiting_draft (shown separately)
  const historyMessages = messages.filter(
    (msg) => !(msg.status === 'pending_approval' && !msg.is_superseded)
      && msg.status !== 'awaiting_draft'
  );

  return (
    <>
      {historyMessages.map((msg) => {
        const isSuperSeded = msg.is_superseded;
        const responseText = msg.edited_response || msg.draft_response;
        const hasResponse = responseText && responseText.length > 0;

        return (
          <div
            key={msg.id}
            className="flex flex-col gap-2"
            data-testid={`thread-message-${msg.id}`}
          >
            {/* Their message (left) */}
            <div className="flex justify-start">
              <div className="max-w-[80%] space-y-1">
                <div className="rounded-lg bg-muted p-3 text-sm">
                  <p className="whitespace-pre-wrap">{msg.incoming_message}</p>
                </div>
                <span className="text-[10px] text-muted-foreground">
                  {formatDateTime(msg.incoming_at)}
                </span>
              </div>
            </div>

            {/* Our response (right) — skip if empty */}
            {hasResponse && (
              <div className="flex justify-end">
                <div className="max-w-[80%] space-y-1">
                  <div
                    className={`rounded-lg p-3 text-sm ${
                      msg.status === 'sent'
                        ? 'bg-primary/10 text-foreground'
                        : isSuperSeded
                        ? 'bg-muted/40 text-muted-foreground'
                        : msg.status === 'rejected'
                        ? 'bg-destructive/5 text-muted-foreground line-through'
                        : 'bg-muted text-foreground'
                    }`}
                  >
                    <p className="whitespace-pre-wrap">{responseText}</p>
                    <div className="mt-2 flex items-center gap-2 text-muted-foreground">
                      {getStatusIcon(msg.status, isSuperSeded)}
                      {msg.methodology_used && (
                        <Badge variant="outline" className="text-[10px]">
                          {msg.methodology_used}
                        </Badge>
                      )}
                      {msg.confidence > 0 && (
                        <span className="text-[10px]">
                          {Math.round(msg.confidence * 100)}%
                        </span>
                      )}
                      {isSuperSeded && (
                        <Badge variant="secondary" className="text-[10px]">
                          Superseded
                        </Badge>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center justify-end gap-1">
                    <span className="text-[10px] text-muted-foreground">
                      {msg.sent_at ? formatDateTime(msg.sent_at) : formatDateTime(msg.created_at)}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────────────────────────────────────

export function ThreadTimeline({
  messages,
  initialOutreach,
  conversationMessages,
  isLoadingHistory,
}: ThreadTimelineProps): JSX.Element {
  const sentDrafts = messages.filter(
    (m) => m.status === 'sent' && !m.is_superseded
  );

  const hasConversation = conversationMessages && conversationMessages.length > 0;

  return (
    <div className="flex flex-col gap-3 py-4">
      {/* Loading state */}
      {isLoadingHistory && !hasConversation && <TimelineSkeleton />}

      {/* Primary: HeyReach conversation history (includes ALL messages) */}
      {hasConversation && (
        <ConversationView
          conversationMessages={conversationMessages}
          sentDrafts={sentDrafts}
        />
      )}

      {/* Fallback: Draft-based rendering (when HeyReach unavailable) */}
      {!hasConversation && !isLoadingHistory && (
        <>
          {initialOutreach && (
            <div className="flex justify-end">
              <div className="max-w-[80%] space-y-1">
                <div className="rounded-lg border border-primary/20 bg-primary/5 p-3 text-sm">
                  <p className="text-xs font-medium text-muted-foreground mb-1">Your initial message</p>
                  <p
                    className="whitespace-pre-wrap text-foreground"
                    data-content-origin="generated"
                  >
                    {initialOutreach}
                  </p>
                </div>
              </div>
            </div>
          )}
          <DraftFallbackView messages={messages} />
        </>
      )}
    </div>
  );
}
